import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Sidebar } from '../components/Sidebar';
import { Navbar } from '../components/Navbar';
import { Users, Ticket, FileText, TrendingUp, Clock, AlertCircle, Building2, Target, ChevronRight, Calendar, Wrench, Award, CheckCircle2, XCircle, Megaphone, Trophy, Medal } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useDashboard } from '../hooks';

export const Dashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { stats, loading } = useDashboard();
  const { user } = useAuth();
  const navigate = useNavigate();

  const getRoleTitle = () => {
    const titles = {
      admin: 'Admin Dashboard',
      manager: 'Manager Dashboard',
      sales_executive: 'Sales Dashboard',
      service_executive: 'Service Dashboard'
    };
    return titles[user?.role] || 'Dashboard';
  };

  const getRoleDescription = () => {
    const descriptions = {
      admin: 'Complete overview of your CRM metrics',
      manager: 'Your team performance and activities',
      sales_executive: 'Your sales pipeline and customer enquiries',
      service_executive: 'Your assigned tickets and support activities'
    };
    return descriptions[user?.role] || 'Overview of your CRM metrics';
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'text-blue-900 bg-blue-100',
      in_progress: 'text-slate-900 bg-white border border-slate-300',
      waiting: 'text-purple-900 bg-purple-100',
      closed: 'text-green-900 bg-green-100',
      resolved: 'text-green-900 bg-green-100',
      new: 'text-blue-900 bg-blue-100',
      contacted: 'text-indigo-900 bg-indigo-100',
      qualified: 'text-purple-900 bg-purple-100',
      proposal_sent: 'text-slate-900 bg-white border border-slate-300',
      won: 'text-green-900 bg-green-100',
      lost: 'text-red-900 bg-red-100',
    };
    return colors[status] || 'text-slate-900 bg-slate-100';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'text-slate-900 bg-slate-100',
      medium: 'text-slate-900 bg-white border border-slate-300',
      high: 'text-red-900 bg-red-100',
    };
    return colors[priority] || 'text-slate-900 bg-slate-100';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar open={sidebarOpen} setOpen={setSidebarOpen} />
      <Navbar setSidebarOpen={setSidebarOpen} />
      
      <main className="md:ml-64 pt-16">
        <div className="p-6 md:p-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-slate-900 mb-2" data-testid="dashboard-title">{getRoleTitle()}</h1>
            <p className="text-slate-600">{getRoleDescription()}</p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
            <div className="bg-white rounded-xl border border-slate-200 p-6 card-hover" data-testid="stat-card-customers">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Users className="h-6 w-6 text-blue-600" />
                </div>
                <TrendingUp className="h-5 w-5 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-1">{stats?.total_customers || 0}</h3>
              <p className="text-sm text-slate-600">Total Customers</p>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-6 card-hover" data-testid="stat-card-tickets">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-orange-100 rounded-lg">
                  <Ticket className="h-6 w-6 text-orange-600" />
                </div>
                <AlertCircle className="h-5 w-5 text-orange-600" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-1">{stats?.open_tickets || 0}</h3>
              <p className="text-sm text-slate-600">Open Tickets</p>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-6 card-hover" data-testid="stat-card-enquiries">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <FileText className="h-6 w-6 text-purple-600" />
                </div>
                <Clock className="h-5 w-5 text-purple-600" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-1">{stats?.active_enquiries || 0}</h3>
              <p className="text-sm text-slate-600">Active Enquiries</p>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-6 card-hover" data-testid="stat-card-marketing-leads">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-pink-100 rounded-lg">
                  <Megaphone className="h-6 w-6 text-pink-600" />
                </div>
                <Clock className="h-5 w-5 text-pink-600" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-1">{stats?.active_marketing_leads || 0}</h3>
              <p className="text-sm text-slate-600">Active Marketing Leads</p>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-6 card-hover" data-testid="stat-card-conversion">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-green-100 rounded-lg">
                  <Target className="h-6 w-6 text-green-600" />
                </div>
                <TrendingUp className="h-5 w-5 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-1">{stats?.lead_conversion_rate || 0}%</h3>
              <p className="text-sm text-slate-600">Lead Conversion Rate</p>
            </div>
          </div>

          {/* Personal Achievement Section */}
          {stats?.personal_stats && (
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl border border-indigo-200 p-6 mb-8" data-testid="personal-achievement-card">
              <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Award className="h-5 w-5 text-indigo-600" />
                My Performance
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* My Enquiries Stats */}
                <div className="bg-white rounded-lg border border-indigo-100 p-4">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <FileText className="h-4 w-4 text-purple-600" />
                    My Enquiries
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div 
                      className="text-center p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors"
                      onClick={() => navigate(`/enquiries?assignedTo=${user?.id}`)}
                      data-testid="my-total-enquiries-link"
                    >
                      <p className="text-2xl font-bold text-slate-900">{stats.personal_stats.my_total_enquiries}</p>
                      <p className="text-xs text-slate-600">Total Assigned</p>
                    </div>
                    <div 
                      className="text-center p-3 bg-blue-50 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors"
                      onClick={() => navigate(`/enquiries?assignedTo=${user?.id}&status=active`)}
                      data-testid="my-active-enquiries-link"
                    >
                      <p className="text-2xl font-bold text-blue-600">{stats.personal_stats.my_active_enquiries}</p>
                      <p className="text-xs text-slate-600">Active</p>
                    </div>
                    <div 
                      className="text-center p-3 bg-green-50 rounded-lg cursor-pointer hover:bg-green-100 transition-colors"
                      onClick={() => navigate(`/enquiries?assignedTo=${user?.id}&status=won`)}
                      data-testid="my-won-enquiries-link"
                    >
                      <div className="flex items-center justify-center gap-1">
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                        <p className="text-2xl font-bold text-green-600">{stats.personal_stats.my_won_enquiries}</p>
                      </div>
                      <p className="text-xs text-slate-600">Won</p>
                    </div>
                    <div 
                      className="text-center p-3 bg-red-50 rounded-lg cursor-pointer hover:bg-red-100 transition-colors"
                      onClick={() => navigate(`/enquiries?assignedTo=${user?.id}&status=lost`)}
                      data-testid="my-lost-enquiries-link"
                    >
                      <div className="flex items-center justify-center gap-1">
                        <XCircle className="h-4 w-4 text-red-600" />
                        <p className="text-2xl font-bold text-red-600">{stats.personal_stats.my_lost_enquiries}</p>
                      </div>
                      <p className="text-xs text-slate-600">Lost</p>
                    </div>
                  </div>
                  <div className="mt-4 pt-3 border-t border-slate-200">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-600">My Conversion Rate</span>
                      <span className={`text-lg font-bold ${
                        stats.personal_stats.my_conversion_rate >= 30 ? 'text-green-600' :
                        stats.personal_stats.my_conversion_rate >= 15 ? 'text-yellow-600' :
                        'text-red-600'
                      }`}>
                        {stats.personal_stats.my_conversion_rate}%
                      </span>
                    </div>
                  </div>
                </div>

                {/* My Tickets Stats */}
                <div className="bg-white rounded-lg border border-indigo-100 p-4">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <Ticket className="h-4 w-4 text-orange-600" />
                    My Tickets
                  </h3>
                  <div className="grid grid-cols-3 gap-3">
                    <div 
                      className="text-center p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors"
                      onClick={() => navigate(`/tickets?assignedTo=${user?.id}`)}
                      data-testid="my-total-tickets-link"
                    >
                      <p className="text-2xl font-bold text-slate-900">{stats.personal_stats.my_total_tickets}</p>
                      <p className="text-xs text-slate-600">Total Assigned</p>
                    </div>
                    <div 
                      className="text-center p-3 bg-orange-50 rounded-lg cursor-pointer hover:bg-orange-100 transition-colors"
                      onClick={() => navigate(`/tickets?assignedTo=${user?.id}&status=open`)}
                      data-testid="my-open-tickets-link"
                    >
                      <p className="text-2xl font-bold text-orange-600">{stats.personal_stats.my_open_tickets}</p>
                      <p className="text-xs text-slate-600">Open</p>
                    </div>
                    <div 
                      className="text-center p-3 bg-green-50 rounded-lg cursor-pointer hover:bg-green-100 transition-colors"
                      onClick={() => navigate(`/tickets?assignedTo=${user?.id}&status=closed`)}
                      data-testid="my-closed-tickets-link"
                    >
                      <p className="text-2xl font-bold text-green-600">{stats.personal_stats.my_closed_tickets}</p>
                      <p className="text-xs text-slate-600">Closed</p>
                    </div>
                  </div>
                  <div className="mt-4 pt-3 border-t border-slate-200">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-600">Resolution Rate</span>
                      <span className={`text-lg font-bold ${
                        stats.personal_stats.my_total_tickets > 0 && 
                        (stats.personal_stats.my_closed_tickets / stats.personal_stats.my_total_tickets * 100) >= 50 
                          ? 'text-green-600' 
                          : 'text-slate-600'
                      }`}>
                        {stats.personal_stats.my_total_tickets > 0 
                          ? Math.round(stats.personal_stats.my_closed_tickets / stats.personal_stats.my_total_tickets * 100) 
                          : 0}%
                      </span>
                    </div>
                  </div>
                </div>

                {/* My Marketing Leads Stats */}
                <div className="bg-white rounded-lg border border-indigo-100 p-4">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <Megaphone className="h-4 w-4 text-pink-600" />
                    My Marketing Leads
                  </h3>
                  <div className="grid grid-cols-3 gap-3">
                    <div 
                      className="text-center p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors"
                      onClick={() => navigate(`/marketing-leads?assignedTo=${user?.id}`)}
                      data-testid="my-total-marketing-leads-link"
                    >
                      <p className="text-2xl font-bold text-slate-900">{stats.personal_stats.my_total_marketing_leads || 0}</p>
                      <p className="text-xs text-slate-600">Total Assigned</p>
                    </div>
                    <div 
                      className="text-center p-3 bg-pink-50 rounded-lg cursor-pointer hover:bg-pink-100 transition-colors"
                      onClick={() => navigate(`/marketing-leads?assignedTo=${user?.id}&status=active`)}
                      data-testid="my-active-marketing-leads-link"
                    >
                      <p className="text-2xl font-bold text-pink-600">{stats.personal_stats.my_active_marketing_leads || 0}</p>
                      <p className="text-xs text-slate-600">Active</p>
                    </div>
                    <div 
                      className="text-center p-3 bg-green-50 rounded-lg cursor-pointer hover:bg-green-100 transition-colors"
                      onClick={() => navigate(`/marketing-leads?assignedTo=${user?.id}&status=won`)}
                      data-testid="my-won-marketing-leads-link"
                    >
                      <div className="flex items-center justify-center gap-1">
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                        <p className="text-2xl font-bold text-green-600">{stats.personal_stats.my_won_marketing_leads || 0}</p>
                      </div>
                      <p className="text-xs text-slate-600">Won</p>
                    </div>
                  </div>
                  <div className="mt-4 pt-3 border-t border-slate-200">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-600">Conversion Rate</span>
                      <span className={`text-lg font-bold ${
                        stats.personal_stats.my_total_marketing_leads > 0 && 
                        ((stats.personal_stats.my_won_marketing_leads || 0) / stats.personal_stats.my_total_marketing_leads * 100) >= 30 
                          ? 'text-green-600' 
                          : 'text-slate-600'
                      }`}>
                        {stats.personal_stats.my_total_marketing_leads > 0 
                          ? Math.round((stats.personal_stats.my_won_marketing_leads || 0) / stats.personal_stats.my_total_marketing_leads * 100) 
                          : 0}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Team Leaderboard - Current Month */}
          {stats?.team_leaderboard && stats.team_leaderboard.length > 0 && (
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-200 p-6 mb-8" data-testid="team-leaderboard-card">
              <h2 className="text-xl font-semibold text-slate-900 mb-2 flex items-center gap-2">
                <Trophy className="h-5 w-5 text-amber-600" />
                Team Leaderboard
              </h2>
              <p className="text-sm text-slate-600 mb-4">Top performers this month (based on conversion rate, deals won, and tickets resolved)</p>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-white/50 border-b border-amber-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Rank</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Team Member</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">Conversion Rate</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">Deals Won</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">Tickets Resolved</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-amber-100">
                    {stats.team_leaderboard.map((member, index) => (
                      <tr key={member.user_id} className={`hover:bg-white/50 transition-colors ${index < 3 ? 'bg-white/30' : ''}`}>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            {index === 0 && <Medal className="h-5 w-5 text-yellow-500" />}
                            {index === 1 && <Medal className="h-5 w-5 text-slate-400" />}
                            {index === 2 && <Medal className="h-5 w-5 text-amber-600" />}
                            <span className={`font-bold ${index < 3 ? 'text-lg' : 'text-base'} ${
                              index === 0 ? 'text-yellow-600' : 
                              index === 1 ? 'text-slate-500' : 
                              index === 2 ? 'text-amber-700' : 'text-slate-700'
                            }`}>
                              #{member.rank}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div>
                            <p className={`font-semibold ${index < 3 ? 'text-slate-900' : 'text-slate-700'}`}>
                              {member.user_name}
                            </p>
                            <p className="text-xs text-slate-500">
                              {member.role?.replace('_', ' ')} • {member.branch_name}
                            </p>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-bold ${
                            member.conversion_rate >= 50 ? 'bg-green-100 text-green-800' :
                            member.conversion_rate >= 30 ? 'bg-blue-100 text-blue-800' :
                            member.conversion_rate >= 15 ? 'bg-yellow-100 text-yellow-800' :
                            'bg-slate-100 text-slate-700'
                          }`}>
                            {member.conversion_rate}%
                          </span>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <CheckCircle2 className="h-4 w-4 text-green-600" />
                            <span className="font-semibold text-green-700">{member.deals_won}</span>
                            <span className="text-xs text-slate-500">/ {member.total_closed}</span>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Ticket className="h-4 w-4 text-purple-600" />
                            <span className="font-semibold text-purple-700">{member.tickets_resolved}</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {stats.team_leaderboard.length === 0 && (
                <div className="text-center py-8">
                  <Trophy className="h-12 w-12 text-amber-300 mx-auto mb-3" />
                  <p className="text-slate-600">No performance data for this month yet.</p>
                  <p className="text-sm text-slate-500 mt-1">Close some deals or resolve tickets to appear on the leaderboard!</p>
                </div>
              )}
            </div>
          )}

          {/* Branch-wise Lead Stats */}
          {stats?.branch_stats && stats.branch_stats.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-6 mb-8" data-testid="branch-stats-card">
              <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                {user?.role === 'admin' ? 'Branch-wise Lead Performance' : 'Your Branch Performance'}
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Branch</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">Total Leads</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">Won Leads</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">Conversion Rate</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {stats.branch_stats.map((branch, index) => (
                      <tr key={branch.branch_id || index} className="hover:bg-slate-50">
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-slate-500" />
                            <span className="text-sm font-medium text-slate-900">{branch.branch_name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <span className="text-sm font-semibold text-slate-900">{branch.total_leads}</span>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <span className="text-sm font-semibold text-green-600">{branch.won_leads}</span>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                            branch.conversion_rate >= 30 ? 'bg-green-100 text-green-900' :
                            branch.conversion_rate >= 15 ? 'bg-yellow-100 text-yellow-900' :
                            'bg-red-100 text-red-900'
                          }`}>
                            {branch.conversion_rate}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {user?.role === 'admin' && stats.branch_stats.length > 1 && (
                    <tfoot className="bg-slate-100 border-t-2 border-slate-300">
                      <tr>
                        <td className="px-4 py-3 font-semibold text-slate-900">Total</td>
                        <td className="px-4 py-3 text-center font-semibold text-slate-900">{stats.total_leads}</td>
                        <td className="px-4 py-3 text-center font-semibold text-green-600">{stats.won_leads}</td>
                        <td className="px-4 py-3 text-center">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-900">
                            {stats.lead_conversion_rate}%
                          </span>
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          )}

          {/* Detailed Stats */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Tickets by Status */}
            <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="tickets-status-card">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">Tickets by Status</h2>
              <div className="space-y-3">
                {Object.entries(stats?.tickets_by_status || {}).map(([status, count]) => (
                  <div key={status} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-blue-600"></div>
                      <span className="text-sm font-medium text-slate-800 capitalize">{status.replace('_', ' ')}</span>
                    </div>
                    <span className="text-sm font-bold text-slate-900">{count}</span>
                  </div>
                ))}
                {Object.keys(stats?.tickets_by_status || {}).length === 0 && (
                  <p className="text-sm text-slate-600">No tickets yet</p>
                )}
              </div>
            </div>

            {/* Enquiries by Status */}
            <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="enquiries-status-card">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">Enquiries by Status</h2>
              <div className="space-y-3">
                {Object.entries(stats?.enquiries_by_status || {}).map(([status, count]) => (
                  <div key={status} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-blue-600"></div>
                      <span className="text-sm font-medium text-slate-800 capitalize">{status.replace('_', ' ')}</span>
                    </div>
                    <span className="text-sm font-bold text-slate-900">{count}</span>
                  </div>
                ))}
                {Object.keys(stats?.enquiries_by_status || {}).length === 0 && (
                  <p className="text-sm text-slate-600">No enquiries yet</p>
                )}
              </div>
            </div>

            {/* Marketing Leads by Status */}
            <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="marketing-leads-status-card">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">Marketing Leads by Status</h2>
              <div className="space-y-3">
                {Object.entries(stats?.marketing_leads_by_status || {}).map(([status, count]) => (
                  <div key={status} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-pink-600"></div>
                      <span className="text-sm font-medium text-slate-800 capitalize">{status.replace('_', ' ')}</span>
                    </div>
                    <span className="text-sm font-bold text-slate-900">{count}</span>
                  </div>
                ))}
                {Object.keys(stats?.marketing_leads_by_status || {}).length === 0 && (
                  <p className="text-sm text-slate-600">No marketing leads yet</p>
                )}
              </div>
            </div>
          </div>

          {/* Pending Enquiries & Tickets Lists */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Pending Enquiries List */}
            <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="pending-enquiries-card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                  <FileText className="h-5 w-5 text-purple-600" />
                  Pending Enquiries
                </h2>
                <Link to="/enquiries" className="text-sm text-accent hover:underline flex items-center gap-1">
                  View All <ChevronRight className="h-4 w-4" />
                </Link>
              </div>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {stats?.pending_enquiries_list?.length > 0 ? (
                  stats.pending_enquiries_list.slice(0, 10).map((enquiry) => (
                    <Link
                      key={enquiry.id}
                      to={`/enquiries`}
                      className="block p-3 rounded-lg border border-slate-200 hover:border-purple-300 hover:bg-purple-50/30 transition-all duration-200"
                      data-testid={`pending-enquiry-${enquiry.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-semibold text-slate-900 truncate">{enquiry.product_name || 'Unknown Product'}</h4>
                          <p className="text-xs text-slate-600 mt-1">
                            Customer: <span className="font-medium">{enquiry.customer_name}</span>
                          </p>
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${getStatusColor(enquiry.status)}`}>
                              {enquiry.status?.replace('_', ' ')}
                            </span>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${getPriorityColor(enquiry.priority)}`}>
                              {enquiry.priority}
                            </span>
                            {user?.role === 'admin' && enquiry.branch_name && enquiry.branch_name !== 'Unassigned' && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                                <Building2 className="h-3 w-3" />
                                {enquiry.branch_name}
                              </span>
                            )}
                          </div>
                          {enquiry.follow_up_date && (
                            <div className="flex items-center gap-1 mt-2 text-xs text-orange-600">
                              <Calendar className="h-3 w-3" />
                              Follow-up: {new Date(enquiry.follow_up_date).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                        <span className="text-xs text-slate-500 whitespace-nowrap">{formatDate(enquiry.created_at)}</span>
                      </div>
                    </Link>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <FileText className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">No pending enquiries</p>
                  </div>
                )}
              </div>
            </div>

            {/* Pending Tickets List */}
            <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="pending-tickets-card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                  <Ticket className="h-5 w-5 text-orange-600" />
                  Pending Tickets
                </h2>
                <Link to="/tickets" className="text-sm text-accent hover:underline flex items-center gap-1">
                  View All <ChevronRight className="h-4 w-4" />
                </Link>
              </div>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {stats?.pending_tickets_list?.length > 0 ? (
                  stats.pending_tickets_list.slice(0, 10).map((ticket) => (
                    <Link
                      key={ticket.id}
                      to={`/tickets`}
                      className="block p-3 rounded-lg border border-slate-200 hover:border-orange-300 hover:bg-orange-50/30 transition-all duration-200"
                      data-testid={`pending-ticket-${ticket.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-semibold text-slate-900 truncate">{ticket.product_name || 'Unknown Product'}</h4>
                          <p className="text-xs text-slate-600 mt-1">
                            Customer: <span className="font-medium">{ticket.customer_name}</span>
                          </p>
                          {ticket.service_center_complaint_number && (
                            <p className="text-xs text-slate-500 mt-0.5">
                              Complaint #: {ticket.service_center_complaint_number}
                            </p>
                          )}
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${getStatusColor(ticket.status)}`}>
                              {ticket.status?.replace('_', ' ')}
                            </span>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${getPriorityColor(ticket.priority)}`}>
                              {ticket.priority}
                            </span>
                            {ticket.warranty_status && (
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                                ticket.warranty_status === 'under_warranty' ? 'bg-green-100 text-green-700' :
                                ticket.warranty_status === 'extended_warranty' ? 'bg-blue-100 text-blue-700' :
                                'bg-slate-100 text-slate-700'
                              }`}>
                                <Wrench className="h-3 w-3" />
                                {ticket.warranty_status?.replace('_', ' ')}
                              </span>
                            )}
                            {user?.role === 'admin' && ticket.branch_name && ticket.branch_name !== 'Unassigned' && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                                <Building2 className="h-3 w-3" />
                                {ticket.branch_name}
                              </span>
                            )}
                          </div>
                        </div>
                        <span className="text-xs text-slate-500 whitespace-nowrap">{formatDate(ticket.created_at)}</span>
                      </div>
                    </Link>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <Ticket className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">No pending tickets</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Pending Marketing Leads List */}
          {stats?.pending_marketing_leads_list?.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-6 mb-8" data-testid="pending-marketing-leads-card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                  <Megaphone className="h-5 w-5 text-pink-600" />
                  Pending Marketing Leads
                </h2>
                <Link to="/marketing-leads" className="text-sm text-accent hover:underline flex items-center gap-1">
                  View All <ChevronRight className="h-4 w-4" />
                </Link>
              </div>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {stats.pending_marketing_leads_list.slice(0, 10).map((lead) => (
                  <Link
                    key={lead.id}
                    to={`/marketing-leads/${lead.id}`}
                    className="block p-3 rounded-lg border border-slate-200 hover:border-pink-300 hover:bg-pink-50/30 transition-all duration-200"
                    data-testid={`pending-marketing-lead-${lead.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-semibold text-slate-900 truncate">{lead.campaign_name || 'Unknown Campaign'}</h4>
                        <p className="text-xs text-slate-600 mt-1">
                          Customer: <span className="font-medium">{lead.customer_name}</span>
                          {lead.customer_phone && <span className="ml-2 text-slate-500">({lead.customer_phone})</span>}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          Product: {lead.product_interest} • Source: {lead.source}
                        </p>
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${getStatusColor(lead.status)}`}>
                            {lead.status?.replace('_', ' ')}
                          </span>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${getPriorityColor(lead.priority)}`}>
                            {lead.priority}
                          </span>
                          {lead.estimated_value && (
                            <span className="text-xs font-medium text-green-600">
                              ₹{lead.estimated_value.toLocaleString()}
                            </span>
                          )}
                          {lead.follow_up_date && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                              <Calendar className="h-3 w-3" />
                              {new Date(lead.follow_up_date).toLocaleDateString()}
                            </span>
                          )}
                          {user?.role === 'admin' && lead.branch_name && lead.branch_name !== 'Unassigned' && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                              <Building2 className="h-3 w-3" />
                              {lead.branch_name}
                            </span>
                          )}
                        </div>
                      </div>
                      <span className="text-xs text-slate-500 whitespace-nowrap">{formatDate(lead.created_at)}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Recent Activity */}
          <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="recent-activity-card">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">Recent Activity</h2>
            <div className="space-y-3">
              {stats?.recent_activities?.map((activity) => (
                <Link
                  key={activity.id}
                  to={`/${activity.type === 'ticket' ? 'tickets' : 'enquiries'}/${activity.id}`}
                  className="block p-4 rounded-lg border border-slate-200 hover:border-accent hover:shadow-sm transition-all duration-200"
                  data-testid={`activity-${activity.type}-${activity.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        {activity.type === 'ticket' ? (
                          <Ticket className="h-4 w-4 text-slate-700" />
                        ) : (
                          <FileText className="h-4 w-4 text-slate-700" />
                        )}
                        <span className="text-xs font-semibold text-slate-600 uppercase">{activity.type}</span>
                      </div>
                      <h3 className="font-semibold text-slate-900 mb-2">{activity.title}</h3>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${getStatusColor(activity.status)}`}>
                          {activity.status.replace('_', ' ')}
                        </span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${getPriorityColor(activity.priority)}`}>
                          {activity.priority}
                        </span>
                      </div>
                    </div>
                    <span className="text-xs text-slate-700">{formatDate(activity.created_at)}</span>
                  </div>
                </Link>
              ))}
              {(!stats?.recent_activities || stats.recent_activities.length === 0) && (
                <p className="text-sm text-slate-500 text-center py-8">No recent activity</p>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};